// SPDX-License-Identifier: AGPL-3.0-or-later

import {Platform} from '@app/features/platform/types/Platform';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {CameraPermissionDeniedModal} from '@app/features/voice/components/alerts/CameraPermissionDeniedModal';
import {MicrophonePermissionDeniedModal} from '@app/features/voice/components/alerts/MicrophonePermissionDeniedModal';
import {mediaDeviceCache} from '@app/features/voice/devices/MediaDeviceCache';
import VoiceDevicePermissionState from '@app/features/voice/engine/VoiceDevicePermissionState';
import MediaPermission from '@app/features/voice/state/MediaPermission';
import NativePermission from '@app/features/voice/state/NativePermission';
import {ensureNativePermission} from '@app/features/voice/utils/NativePermissions';
import type {VoiceDeviceState} from '@app/features/voice/utils/VoiceDeviceManager';
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

type PermissionType = 'audio' | 'video';
type BrowserPermissionState = 'denied' | 'granted' | 'prompt';

interface PermissionState {
	status: 'idle' | 'loading' | 'granted' | 'denied';
	devices: Array<MediaDeviceInfo>;
}

interface UseMediaPermissionOptions {
	autoRequest?: boolean;
}

const filterDevicesForType = (devices: Array<MediaDeviceInfo>, type: PermissionType) => {
	if (type === 'audio') {
		return devices.filter((d) => d.kind === 'audioinput' || d.kind === 'audiooutput');
	}
	return devices.filter((d) => d.kind === 'videoinput');
};
const devicesFromVoiceState = (deviceState: VoiceDeviceState, type: PermissionType): Array<MediaDeviceInfo> => {
	if (type === 'audio') {
		return [...deviceState.inputDevices, ...deviceState.outputDevices];
	}
	return deviceState.videoDevices;
};
const resolveStatusFromVoiceState = ({
	autoRequest,
	cachedPermissionState,
	deviceState,
	isExplicitlyDenied,
	storeInitialized,
}: {
	autoRequest: boolean;
	cachedPermissionState: BrowserPermissionState | null;
	deviceState: VoiceDeviceState;
	isExplicitlyDenied: boolean;
	storeInitialized: boolean;
}): PermissionState['status'] => {
	if (isExplicitlyDenied) return 'denied';
	if (deviceState.permissionStatus === 'loading') return 'loading';
	if (deviceState.permissionStatus === 'granted') return 'granted';
	if (deviceState.permissionStatus === 'denied') return 'denied';
	if (cachedPermissionState === 'granted') return 'loading';
	if (!storeInitialized && autoRequest) return 'loading';
	return 'idle';
};
const needsBrowserPermission = (devices: Array<MediaDeviceInfo>, type: PermissionType) => {
	if (devices.length === 0) return true;
	const requiredKind = type === 'audio' ? 'audioinput' : 'videoinput';
	const hasPrimaryDevice = devices.some((device) => device.kind === requiredKind);
	const hasLabels = devices.some((device) => (device.label ?? '').trim().length > 0);
	return !hasPrimaryDevice || !hasLabels;
};
const hasPrimaryDeviceForType = (devices: Array<MediaDeviceInfo>, type: PermissionType) => {
	const requiredKind = type === 'audio' ? 'audioinput' : 'videoinput';
	return devices.some((device) => device.kind === requiredKind);
};
export const useMediaPermission = (type: PermissionType, options: UseMediaPermissionOptions = {}) => {
	const {autoRequest = true} = options;
	const micExplicitlyDenied = MediaPermission.microphoneExplicitlyDenied;
	const cameraExplicitlyDenied = MediaPermission.cameraExplicitlyDenied;
	const isExplicitlyDenied = type === 'audio' ? micExplicitlyDenied : cameraExplicitlyDenied;
	const cachedPermissionState =
		type === 'audio' ? MediaPermission.microphonePermissionState : MediaPermission.cameraPermissionState;
	const storeInitialized = MediaPermission.initialized;
	const initialDeviceState = VoiceDevicePermissionState.getState();
	const [state, setState] = useState<PermissionState>(() => ({
		status: resolveStatusFromVoiceState({
			autoRequest,
			cachedPermissionState,
			deviceState: initialDeviceState,
			isExplicitlyDenied,
			storeInitialized,
		}),
		devices: devicesFromVoiceState(initialDeviceState, type),
	}));
	const isDesktop = NativePermission.isDesktop;
	const applyVoiceDeviceState = useCallback(
		(deviceState: VoiceDeviceState): PermissionState => {
			const nextState = {
				status: resolveStatusFromVoiceState({
					autoRequest,
					cachedPermissionState,
					deviceState,
					isExplicitlyDenied,
					storeInitialized,
				}),
				devices: devicesFromVoiceState(deviceState, type),
			};
			setState(nextState);
			return nextState;
		},
		[autoRequest, cachedPermissionState, isExplicitlyDenied, storeInitialized, type],
	);
	const fetchDevices = useCallback(async (): Promise<Array<MediaDeviceInfo>> => {
		const ensureBrowserPermission = async () => {
			const constraints = type === 'audio' ? {audio: true} : {video: true};
			const stream = await navigator.mediaDevices.getUserMedia(constraints);
			stream.getTracks().forEach((track) => track.stop());
		};
		const enumerateFiltered = async () => {
			const rawDevices = await navigator.mediaDevices.enumerateDevices();
			return filterDevicesForType(rawDevices, type);
		};
		const isIOSPWA = Platform.isIOSWeb && Platform.isPWA;
		if (isIOSPWA && navigator.permissions) {
			try {
				const permissionName = type === 'audio' ? 'microphone' : 'camera';
				const permission = await navigator.permissions.query({name: permissionName as PermissionName});
				if (permission.state === 'granted') {
					const devices = await enumerateFiltered();
					if (devices.length > 0 && devices.some((d) => (d.label ?? '').trim().length > 0)) {
						return devices;
					}
				}
			} catch {}
		}
		let shouldPrimeBrowserPermission = !isDesktop;
		if (isDesktop) {
			const nativeResult = await ensureNativePermission(type === 'audio' ? 'microphone' : 'camera');
			if (nativeResult === 'denied') {
				const error = new DOMException('PermissionDenied', 'NotAllowedError');
				throw error;
			}
			shouldPrimeBrowserPermission = nativeResult === 'unsupported';
		}
		let filteredDevices = await enumerateFiltered();
		if (shouldPrimeBrowserPermission || needsBrowserPermission(filteredDevices, type)) {
			await ensureBrowserPermission();
			filteredDevices = await enumerateFiltered();
		}
		return filteredDevices;
	}, [type, isDesktop]);
	const requestPermission = useCallback(async () => {
		if (isExplicitlyDenied) {
			const Modal = type === 'audio' ? MicrophonePermissionDeniedModal : CameraPermissionDeniedModal;
			ModalCommands.push(modal(() => <Modal data-flx="user.use-media-permission.request-permission.modal" />));
			return false;
		}
		const currentDeviceState = VoiceDevicePermissionState.getState();
		const currentDevices = devicesFromVoiceState(currentDeviceState, type);
		if (currentDeviceState.permissionStatus === 'granted' && hasPrimaryDeviceForType(currentDevices, type)) {
			applyVoiceDeviceState(currentDeviceState);
			return true;
		}
		try {
			setState((prev) => ({...prev, status: 'loading'}));
			if (currentDeviceState.permissionStatus === 'loading') {
				const syncedState = applyVoiceDeviceState(
					await VoiceDevicePermissionState.ensureDevices({requestPermissions: true}),
				);
				if (syncedState.status === 'granted' && hasPrimaryDeviceForType(syncedState.devices, type)) {
					return true;
				}
			}
			const {devices} = await mediaDeviceCache.getDevices(type, fetchDevices);
			setState({
				status: 'granted',
				devices,
			});
			if (type === 'audio') {
				MediaPermission.updateMicrophonePermissionGranted({refreshDevices: false});
			} else {
				MediaPermission.updateCameraPermissionGranted({refreshDevices: false});
			}
			void VoiceDevicePermissionState.ensureDevices({requestPermissions: true})
				.then(applyVoiceDeviceState)
				.catch(() => {});
			return true;
		} catch (error) {
			if (error instanceof Error && (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')) {
				const Modal = type === 'audio' ? MicrophonePermissionDeniedModal : CameraPermissionDeniedModal;
				if (type === 'audio') {
					MediaPermission.markMicrophoneExplicitlyDenied();
				} else {
					MediaPermission.markCameraExplicitlyDenied();
				}
				ModalCommands.push(modal(() => <Modal data-flx="user.use-media-permission.request-permission.modal--2" />));
				setState((prev) => ({...prev, status: 'denied'}));
			}
			return false;
		}
	}, [type, isExplicitlyDenied, applyVoiceDeviceState, fetchDevices]);
	const requestPermissionRef = useRef(requestPermission);
	useLayoutEffect(() => {
		requestPermissionRef.current = requestPermission;
	}, [requestPermission]);
	useEffect(() => VoiceDevicePermissionState.subscribe(applyVoiceDeviceState), [applyVoiceDeviceState]);
	useLayoutEffect(() => {
		if (isExplicitlyDenied) {
			setState((prev) => ({...prev, status: 'denied'}));
			return;
		}
		if (!autoRequest) {
			return;
		}
		if (cachedPermissionState === 'granted') {
			void requestPermissionRef.current();
			return;
		}
		if (!storeInitialized) {
			return;
		}
		const currentPermissionState =
			type === 'audio' ? MediaPermission.microphonePermissionState : MediaPermission.cameraPermissionState;
		if (currentPermissionState === 'granted') {
			void requestPermissionRef.current();
		} else {
			setState((prev) => ({...prev, status: 'idle', devices: []}));
		}
	}, [isExplicitlyDenied, type, autoRequest, cachedPermissionState, storeInitialized]);
	return {
		...state,
		isExplicitlyDenied,
		requestPermission,
	};
};
